import { useEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { DS2 } from '../../theme/designTokens';
import { useTranslation } from '../../i18n/react';
import { getCurrentLocale, setLanguage } from '../../i18n';
import { SUPPORTED_LOCALES, type AppLocale } from '../../i18n/locale';
import { useAuth } from '../../context/AuthContext';
import { saveAccountLanguage, useLanguageSaveStatus } from '../../hooks/accountLanguage';
import { renderUiMessage } from '../../i18n/uiMessage';

/**
 * Language selector — ONE locale-state implementation (getCurrentLocale +
 * setLanguage from src/i18n), two presentation variants:
 *
 *   'settings' — two-option segmented control built on NATIVE radio inputs
 *      (a real radiogroup: keyboard, focus, and announcement semantics come
 *      from the platform), styled to the DS2 Settings row look.
 *   'compact'  — globe trigger + small anchored listbox, following the
 *      app's established anchored-dropdown precedent (CountryPicker):
 *      aria-haspopup/expanded trigger, role=listbox panel, Arrow/Enter/
 *      Escape keys, outside-click close, focus returned to the trigger.
 *
 * Options are self-identifying — always "English" / "Español", each tagged
 * with its own lang attribute so screen readers pronounce them correctly.
 * NO flags, ever: languages are not countries. Switching calls setLanguage()
 * only — no navigation, no reload, no network, no auth mutation.
 */

const OPTION_DEFS: ReadonlyArray<{ locale: AppLocale; labelKey: 'language.english' | 'language.spanish'; lang: string }> = [
  { locale: 'en', labelKey: 'language.english', lang: 'en' },
  { locale: 'es-419', labelKey: 'language.spanish', lang: 'es-419' },
];

interface Props {
  variant: 'compact' | 'settings';
  /** Open the compact menu upward (for placements near the viewport bottom). */
  openUp?: boolean;
  /** Icon-only compact trigger (collapsed sidebar). */
  iconOnly?: boolean;
  id?: string;
}

function GlobeIcon({ size = 16, color = 'currentColor' }: { size?: number; color?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="9" stroke={color} strokeWidth="1.6" />
      <ellipse cx="12" cy="12" rx="4" ry="9" stroke={color} strokeWidth="1.6" />
      <path d="M3.5 9h17M3.5 15h17" stroke={color} strokeWidth="1.6" />
    </svg>
  );
}

export function LanguageSelector({ variant, openUp = false, iconOnly = false, id = 'language-selector' }: Props) {
  const { t } = useTranslation('common');
  const { isLoggedIn } = useAuth();
  const save = useLanguageSaveStatus();
  const current = getCurrentLocale();
  // One concise post-switch announcement (in the NEW language) — the rest of
  // the page re-rendering must not turn into live-region noise.
  const [announce, setAnnounce] = useState('');

  async function choose(locale: AppLocale) {
    if (locale === current) return;
    if (isLoggedIn) {
      // Authenticated: optimistic switch + account persistence with rollback
      // and last-choice-wins ordering (accountLanguage seam). The switch
      // announcement fires only if the language actually ended up applied —
      // a rolled-back failure must not announce a change that didn't stick.
      await saveAccountLanguage(locale);
      if (getCurrentLocale() === locale) {
        setAnnounce(locale === 'en' ? 'Language set to English' : 'Idioma cambiado a Español');
      }
      return;
    }
    // Logged out: device-local only — no account, no network. Unchanged.
    await setLanguage(locale);
    setAnnounce(locale === 'en' ? 'Language set to English' : 'Idioma cambiado a Español');
  }

  // Account-save feedback (authenticated only): one concise status line in
  // the DS2 muted style. The error keeps backend prose when supplied (raw
  // precedence) and otherwise localizes.
  const saveLine = isLoggedIn && save.status !== 'idle' ? (
    save.status === 'error' ? (
      <div role="alert" style={{ marginTop: 6, fontSize: 12, fontFamily: DS2.font.body, color: '#E5484D' }}>
        {renderUiMessage(t, save.error) || t('language.saveError')}
      </div>
    ) : (
      <div style={{ marginTop: 6, fontSize: 12, fontFamily: DS2.font.body, color: '#9A9A9A' }}>
        {save.status === 'saving' ? t('language.saving') : t('language.saved')}
      </div>
    )
  ) : null;

  const status = (
    <span
      role="status"
      aria-live="polite"
      style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clipPath: 'inset(50%)', whiteSpace: 'nowrap' }}
    >
      {announce}
      {isLoggedIn && save.status === 'saving' ? ` ${t('language.saving')}` : ''}
      {isLoggedIn && save.status === 'saved' ? ` ${t('language.saved')}` : ''}
    </span>
  );

  if (variant === 'settings') {
    return (
      <div>
      <div
        role="radiogroup"
        aria-label={t('language.label')}
        style={{
          position:     'relative',
          display:      'inline-flex',
          background:   '#131313',
          border:       `1px solid ${DS2.color.gold}`,
          borderRadius: 12,
          padding:      3,
          gap:          3,
        }}
      >
        {OPTION_DEFS.map(opt => {
          const selected = opt.locale === current;
          return (
            <label
              key={opt.locale}
              lang={opt.lang}
              style={{
                display:        'flex',
                alignItems:     'center',
                justifyContent: 'center',
                minWidth:       92,
                minHeight:      36,
                padding:        '7px 18px',
                borderRadius:   9,
                background:     selected ? DS2.gradient.gold : 'transparent',
                color:          '#FFFFFF',
                fontFamily:     DS2.font.body,
                fontWeight:     selected ? 600 : 400,
                fontSize:       14,
                cursor:         selected ? 'default' : 'pointer',
                WebkitTapHighlightColor: 'transparent',
              }}
            >
              <input
                type="radio"
                name={`${id}-radio`}
                checked={selected}
                onChange={() => void choose(opt.locale)}
                style={{ position: 'absolute', width: 1, height: 1, opacity: 0, pointerEvents: 'none' }}
              />
              {t(opt.labelKey)}
            </label>
          );
        })}
        {status}
      </div>
      {saveLine}
      </div>
    );
  }

  return <CompactSelector id={id} openUp={openUp} iconOnly={iconOnly} current={current} choose={choose} status={status} saveLine={saveLine} />;
}

function CompactSelector({
  id, openUp, iconOnly, current, choose, status, saveLine,
}: {
  id: string;
  openUp: boolean;
  iconOnly: boolean;
  current: AppLocale;
  choose: (l: AppLocale) => Promise<void>;
  status: React.ReactNode;
  saveLine: React.ReactNode;
}) {
  const { t } = useTranslation('common');
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const currentLabel = current === 'en' ? t('language.english') : t('language.spanish');

  useEffect(() => {
    if (!open) return;
    setActiveIndex(Math.max(0, SUPPORTED_LOCALES.indexOf(current)));
    function onPointerDown(e: PointerEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open, current]);

  function close(returnFocus: boolean) {
    setOpen(false);
    if (returnFocus) triggerRef.current?.focus();
  }

  async function commit(locale: AppLocale) {
    close(true);
    await choose(locale);
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Escape') { e.stopPropagation(); close(true); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIndex(i => Math.min(i + 1, OPTION_DEFS.length - 1)); return; }
    if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIndex(i => Math.max(i - 1, 0)); return; }
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      void commit(OPTION_DEFS[activeIndex].locale);
    }
  }

  const optionBase: CSSProperties = {
    display:    'flex',
    alignItems: 'center',
    gap:        8,
    width:      '100%',
    minHeight:  40,
    padding:    '8px 14px',
    background: 'transparent',
    border:     'none',
    cursor:     'pointer',
    textAlign:  'left',
    fontFamily: DS2.font.body,
    fontSize:   14,
    color:      '#FFFFFF',
    WebkitTapHighlightColor: 'transparent',
  };

  return (
    <div ref={rootRef} style={{ position: 'relative', width: iconOnly ? undefined : '100%' }} onKeyDown={onKeyDown}>
      <button
        ref={triggerRef}
        type="button"
        id={id}
        onClick={() => setOpen(o => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`${t('language.label')}: ${currentLabel}`}
        title={iconOnly ? currentLabel : undefined}
        style={{
          display:        'flex',
          alignItems:     'center',
          justifyContent: 'center',
          gap:            8,
          width:          iconOnly ? 44 : '100%',
          minHeight:      iconOnly ? 44 : 36,
          padding:        iconOnly ? '10px 8px' : '8px 12px',
          borderRadius:   12,
          background:     '#131313',
          border:         `1px solid ${DS2.color.gold}`,
          color:          '#FFFFFF',
          fontFamily:     DS2.font.body,
          fontWeight:     500,
          fontSize:       13,
          cursor:         'pointer',
          boxSizing:      'border-box',
          WebkitTapHighlightColor: 'transparent',
        }}
      >
        <GlobeIcon size={16} color={DS2.color.gold} />
        {!iconOnly && <span lang={current}>{currentLabel}</span>}
        {!iconOnly && <span aria-hidden="true" style={{ fontSize: 9, color: DS2.color.gold }}>{openUp ? '▴' : '▾'}</span>}
      </button>

      {open && (
        <div
          role="listbox"
          id={`${id}-listbox`}
          aria-label={t('language.label')}
          style={{
            position:     'absolute',
            ...(openUp ? { bottom: 'calc(100% + 6px)' } : { top: 'calc(100% + 6px)' }),
            left:         0,
            zIndex:       60,
            minWidth:     iconOnly ? 140 : '100%',
            background:   '#181818',
            border:       `1px solid ${DS2.color.gold}`,
            borderRadius: 10,
            boxShadow:    '0 12px 32px rgba(0,0,0,0.5)',
            overflow:     'hidden',
            padding:      '4px 0',
          }}
        >
          {OPTION_DEFS.map((opt, i) => {
            const selected = opt.locale === current;
            return (
              <button
                key={opt.locale}
                type="button"
                role="option"
                lang={opt.lang}
                aria-selected={selected}
                onClick={() => void commit(opt.locale)}
                onMouseEnter={() => setActiveIndex(i)}
                style={{
                  ...optionBase,
                  background: activeIndex === i ? 'rgba(255,255,255,0.08)' : 'transparent',
                  fontWeight: selected ? 600 : 400,
                }}
              >
                <span style={{ flex: 1 }}>{opt.locale === 'en' ? t('language.english') : t('language.spanish')}</span>
                {selected && <span aria-hidden="true" style={{ color: DS2.color.gold }}>✓</span>}
              </button>
            );
          })}
        </div>
      )}
      {status}
      {saveLine}
    </div>
  );
}
